import { Button, KvixIcon, Text } from "@kvix/ui";
import {
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { useOnce } from "@react-spring/shared";
import { format } from "date-fns";
import queryString from "query-string";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useMount } from "react-use";
import { isInstructor, KvixUserContext } from "../../../../../contexts/user";
import { Advertisment } from "../../../../partials/Advertisment";
import { HandleCookies } from "../../../../partials/Modal/LanguageCookiePrompt/HandleCookies";
import { CoinImageSize } from "../../../Payment/BuyCoins/enums/CoinImageSize";
import { CoinImage } from "../../../Payment/BuyCoins/partials/CoinImage";
import { PaymentContext } from "../../../Payment/context";
import { useUserBalance } from "../../../Payment/hooks";
import { ProfileSection } from "../../partials/Section";
import { ChangeEmailDialog } from "./dialogs/changeEmailDialog";
import { CheckingAccountsDialog } from "./dialogs/checkingAccountsDialog";
import { DeleteUserDialog } from "./dialogs/deleteUserDialog";
import { EmailUpdateConfirmationDialog } from "./dialogs/emailUpdateConfirmationDialog";
import { BillingRow } from "./sections/billingRow";
import { AccountSectionInvoices } from "./sections/Invoices";
import { AccountSectionPaymentMethod } from "./sections/PaymentMethod";
import { AccountSectionSubscriptions } from "./sections/Subscriptions";

const useActiveSubscriptions = () => {
  const { customer } = useContext(PaymentContext);

  if (!customer || !customer?.subscriptions) {
    return [];
  }

  return customer.subscriptions.data.filter((subscription) => {
    return subscription.cancel_at_period_end === false;
  });
};

export const ProfilePageBilling: React.FC = (props: any) => {
  const { customer } = useContext(PaymentContext);
  const { user, actions } = useContext(KvixUserContext);
  const { t, ready } = useTranslation(["pages", "common"]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [openDeletion, setOpenDeletion] = useState(false);
  const [openCookieSettings, setOpenCookieSettings] = useState<boolean>(false);
  const [openAdvertismentSettings, setOpenAdvertismentSettings] =
    useState(false);
  const [openEmailChange, setOpenEmailChange] = useState(false);
  const [openEmailUpdateConfirmation, setOpenEmailUpdateConfirmation] =
    useState(false);
  const [emailUpdateErrors, setEmailUpdateErrors] = useState([]);
  const { replace, location } = useHistory();

  const activeSubscriptions = useActiveSubscriptions();
  const hasActiveSubscription = activeSubscriptions.length > 0;

  const [checkingAccounts, setCheckingAccounts] = useState([]);

  useOnce(() => {
    actions.getCheckingAccountHistory().then((r) => {
      const tempList = r.sort(
        (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
      );
      setCheckingAccounts(tempList);
    });
  });

  useMount(() => {
    const queryParams = queryString.parse(props.location.search);

    if (queryParams && queryParams["email"] && queryParams["verification"]) {
      confirmEmailChange(queryParams);
    }
  });

  const balance = useUserBalance();
  const [displayBalance, setDisplayBalance] = useState<number>(0);

  useEffect(() => {
    if (balance !== null) {
      setDisplayBalance(balance);
    }
  }, [balance]);

  const confirmEmailChange = async (
    params: queryString.ParsedQuery<string>
  ) => {
    const { email, verification } = params;
    const response = await actions.changeEmail(
      email.toString(),
      verification.toString()
    );
    replace(location.pathname);

    if (response.errors && response.errors.length > 0) {
      setEmailUpdateErrors(response.errors);
    } else {
      user.email = email.toString();
    }
    setOpenEmailUpdateConfirmation(true);
  };

  const createdDate = new Date(user.created);
  const joined = format(createdDate, "dd/MM/yyyy");

  const [openCheckingAccountHistory, setOpenCheckingAccountHistory] =
    useState(false);

  if (!ready) {
    return null;
  }

  return (
    <Grid container direction="column">
      <Grid item>
        <Typography variant="h3">{t("pages:profile.billing.title")}</Typography>
        <Typography variant="body1">
          {t("pages:profile.billing.subtitle")}
        </Typography>
      </Grid>
      <Grid item>
        <Divider light style={{ margin: "10px 0" }} />
      </Grid>
      <Grid item container direction="column">
        <BillingRow
          title={t("pages:profile.billing.email")}
          value={user.email}
          button={
            <Text
              align={isMobile ? "left" : "right"}
              onClick={() => setOpenEmailChange(true)}
              color="primary"
            >
              {t("common:edit")}
            </Text>
          }
        />
        <BillingRow title={t("pages:profile.billing.join")} value={joined} />
        <BillingRow
          title={t("pages:profile.billing.kvixCoins")}
          value={
            <Text align={isMobile ? "left" : "right"}>
              {`${displayBalance} `}
              <CoinImage size={CoinImageSize.MD} />
            </Text>
          }
          button={
            <Text
              align={isMobile ? "left" : "right"}
              onClick={() => setOpenCheckingAccountHistory(true)}
              color="primary"
            >
              {t("pages:profile.billing.kvixCoinsHistory")}
            </Text>
          }
        />
      </Grid>

      {customer && (
        <Fragment>
          <AccountSectionSubscriptions />
          {hasActiveSubscription && <AccountSectionPaymentMethod />}
          <AccountSectionInvoices />
        </Fragment>
      )}

      <ProfileSection title="">
        <Button
          onClick={() => setOpenDeletion(true)}
          variant="text"
          color="primary"
          size="small"
          startIcon={<KvixIcon.Delete />}
          disabled={isInstructor(user)}
          id="deleteaccount"
          lowercase
        >
          {t("pages:profile.billing.deleteMyAccountButton")}
        </Button>
        <Divider light style={{ margin: "10px 0" }} />
        <Button
          onClick={() => setOpenAdvertismentSettings(true)}
          variant="text"
          color="primary"
          size="small"
          startIcon={<KvixIcon.EmailIcon />}
          id="emailsettings"
          lowercase
        >
          {t("pages:profile.billing.advertismentButton")}
        </Button>
        <Divider light style={{ margin: "10px 0" }} />
        <Button
          onClick={() => setOpenCookieSettings(true)}
          variant="text"
          color="primary"
          size="small"
          startIcon={<KvixIcon.Settings />}
          id="cookiesettings"
          lowercase
        >
          {t("pages:profile.billing.cookieSettingsButton")}
        </Button>
      </ProfileSection>
      <DeleteUserDialog
        open={openDeletion}
        user={user}
        fullScreen={isMobile}
        handleOpen={setOpenDeletion}
      />
      <ChangeEmailDialog
        open={openEmailChange}
        user={user}
        fullScreen={isMobile}
        handleOpen={setOpenEmailChange}
      />
      <EmailUpdateConfirmationDialog
        open={openEmailUpdateConfirmation}
        fullScreen={isMobile}
        errors={emailUpdateErrors}
        handleOpen={setOpenEmailUpdateConfirmation}
      />
      <HandleCookies
        manuallyOpen={true}
        open={openCookieSettings}
        handleOpen={setOpenCookieSettings}
      />
      <Advertisment
        open={openAdvertismentSettings}
        onClose={() => setOpenAdvertismentSettings(false)}
      />
      <CheckingAccountsDialog
        open={openCheckingAccountHistory}
        handleOpen={setOpenCheckingAccountHistory}
        checkingAccountList={checkingAccounts}
      />
    </Grid>
  );
};
